import React, { useState, useEffect, useMemo, useRef } from 'react';
import SliderMedia from '@/app/base-ui/SwiperSliderMedia';
import BaseCardMedia from '@/app/base-ui/BaseCardMedia';
import { IMedia } from '@/app/models/media/IMedia';
import { Box, Typography, Grid, useTheme, useMediaQuery } from '@mui/material';
import { AbsoluteValue, PostTypeConfig } from '@/app/constants/typedef';
import FallbackUI from '@/app/components/MUI/FallBackImageUI';
import { DateType } from '@/app/models/date';
import moment from 'moment';
import ModalViewsMedia from '@/app/components/MUI/ModalViewsMedia';
import JoyPlayer from '@/app/base-ui/ReactPlayerVideo';
import CenteredBox from '@/app/base-ui/CenterBox';
import { getYouTubeThumbnail, extractVideoId } from '@/app/components/Writing/UrlFormWriting/components/YouTubeThumbnail';
import Image from 'next/image';
import { MAX_HEIGHT_IMG, MAX_WIDTH_IMG, MIN_HEIGHT_IMG, MIN_WIDTH_IMG } from '@/app/constants/layoutConfig';
import { ChallengeMode, ChallengeModeType } from '@/app/models/challenges/ChallengeModel';
import { PlayButtonYoutube } from '@/public/icon';
import { useTranslation } from '@/app/hooks/useTranslation';

export interface ImageGridAction {
    data?: IMedia[];
    endAt?: DateType;
    type?: PostTypeConfig;
    mode?: ChallengeModeType;
    showBullet?: boolean;
    moveStarted?: (value: boolean) => void;
    isBlockModalViewMedia?: boolean;
    blockOpenUrl?: boolean;
    isUrlChallenge?: boolean;
    url?: any;
    hasUrl?: boolean;
    productStatus?: 'selling' | 'reserved' | 'sold' | null;
    priority?: boolean;
}
const ImageGridAction: React.FC<ImageGridAction> = ({
    data,
    mode,
    endAt,
    isBlockModalViewMedia,
    blockOpenUrl,
    isUrlChallenge,
    url,
    hasUrl,
    showBullet = true,
    type = PostTypeConfig.Challenges,
    moveStarted,
    productStatus,
    priority,
}) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const [endTime, setEndTime] = useState<moment.Moment | null>(null);
    const timeLeftRef = useRef({ hours: 0, minutes: 0, seconds: 0 });
    const [, forceUpdate] = useState({});
    const [activeIndex, setActiveIndex] = useState(1);
    const swiperRef = useRef<any>(null);
    const [isYoutubePlaying, setIsYoutubePlaying] = useState(false);
    const isTablet = useMediaQuery(theme.breakpoints.up('tablet'));

    const shouldShowNumbersImg = useMemo(() => {
        if (!data?.length) return false;
        return data?.length > 1 && [PostTypeConfig.Posts, PostTypeConfig.fetchForMyChannel].includes(type);
    }, [data, type]);

    useEffect(() => {
        if (type === PostTypeConfig.Challenges) {
            const newEndTime = endAt ? moment(endAt) : null;
            setEndTime(newEndTime);
            timeLeftRef.current = calculateTimeLeft(newEndTime);
            forceUpdate({});
        }
    }, [endAt, type]);

    useEffect(() => {
        let timer: any;
        if (type === PostTypeConfig.Challenges && mode !== ChallengeMode.CONTINUOUS) {
            timer = setInterval(() => {
                timeLeftRef.current = calculateTimeLeft(endTime);
                forceUpdate({});
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [endTime, type, mode]);

    function calculateTimeLeft(endTime: moment.Moment | null) {
        const now = moment();
        const difference = endTime ? endTime.diff(now) : 0;

        let timeLeft = {
            hours: 0,
            minutes: 0,
            seconds: 0,
        };

        if (difference > 0) {
            timeLeft = {
                hours: Math.floor(difference / (1000 * 60 * 60)),
                minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
                seconds: Math.floor((difference % (1000 * 60)) / 1000),
            };
        }

        return timeLeft;
    }

    const formatTime = (value: number) => {
        return value < 10 ? `0${value}` : value;
    };

    const onHandleMediaVideoProcess = (allowTouchMove: boolean) => {
        if (swiperRef.current) {
            if (swiperRef.current.pagination && swiperRef.current.pagination.el) {
                if (!allowTouchMove) {
                    swiperRef.current.pagination.el.style.display = 'none';
                } else {
                    swiperRef.current.pagination.el.style.display = 'block';
                }
            }
            swiperRef.current.allowTouchMove = allowTouchMove;
            // swiperRef.current.pagination = false;
        }
    };

    if (isUrlChallenge && hasUrl) {
        const thumbNailYoutube = getYouTubeThumbnail((url as string) ?? '');
        return (
            <CenteredBox
                sx={{
                    py: 0,
                    position: 'relative',
                    minHeight: MIN_HEIGHT_IMG,
                    flexDirection: 'column',
                    height: '100%',
                    minWidth: MIN_WIDTH_IMG,
                    width: '100%',
                    background: theme.palette.background.default,
                }}
            >
                <Grid
                    item
                    className="thumbTiles"
                    style={{ backgroundColor: theme.palette.grayscale.gray02, display: 'flex', justifyContent: 'center', width: '100%' }}
                >
                    <Box
                        position="relative"
                        sx={{ 
                            width: '100%', 
                            height: type === PostTypeConfig.Challenges ? { mobile: '100%', tablet: '768px' } : '100%', 
                            maxHeight: type === PostTypeConfig.Challenges ? { mobile: MAX_HEIGHT_IMG, tablet: '768px' } : { mobile: MAX_HEIGHT_IMG, tablet: '768px' } 
                        }}
                    >
                        <ModalViewsMedia
                            isBlockModalViewMedia={isBlockModalViewMedia}
                            mediaUrl={thumbNailYoutube as string}
                            listMedias={[{ mediaUrl: thumbNailYoutube ?? '', mediaType: 'image' } as any]}
                            initialIndex={1}
                            key={thumbNailYoutube}
                        >
                            <BaseCardMedia
                                src={thumbNailYoutube ?? ''}
                                activeIndex={activeIndex}
                                dataLength={data?.length}
                                alt="Media Feed"
                                // handleVideoReady={onReady}
                                // onHandleMediaVideoProcess={onHandleMediaVideoProcess}
                                mediaType={'image'}
                                shouldShowNumbersImg={shouldShowNumbersImg}
                                // component={'img'}
                                priority={priority}
                                style={{ 
                                    minHeight: MIN_HEIGHT_IMG, 
                                    maxHeight: type === PostTypeConfig.Challenges && isTablet ? '540px' : (isTablet ? '768px' : MAX_HEIGHT_IMG),
                                    width: '100%', 
                                    objectFit: 'cover' 
                                }}
                                onError={() => null}
                            />
                        </ModalViewsMedia>
                        {type === PostTypeConfig.Challenges && url && mode !== ChallengeMode.CONTINUOUS ? (
                            <Box
                                sx={{
                                    width: 'fit-content',
                                    height: '24px',
                                    bottom: 20,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                    borderRadius: '30px',
                                    right: 20,
                                    lineHeight: 0,
                                    p: '7px 8px',
                                    gap: '10px',
                                    zIndex: 99,
                                    position: 'absolute',
                                    color: 'white',
                                    cursor: 'pointer',
                                }}
                            >
                                <Typography
                                    variant="h2_10Regular"
                                    // color="primary.contrastText"
                                >
                                    {timeLeftRef.current.hours === 0 && timeLeftRef.current.minutes === 0 && timeLeftRef.current.seconds === 0
                                        ? t('challenge_ended') || '챌린지 종료'
                                        : `${t('time_remaining') || '남은 시간'} ${formatTime(timeLeftRef.current.hours)}:${formatTime(timeLeftRef.current.minutes)}:${formatTime(timeLeftRef.current.seconds)}`}
                                </Typography>
                            </Box>
                        ) : null}
                    </Box>
                </Grid>
            </CenteredBox>
        );
    }

    if (!data?.length)
        return (
            <Grid
                item
                className="thumbTiles"
                style={{ backgroundColor: 'grayscale.gray02', display: 'flex', justifyContent: 'center' }}
            >
                <Box sx={{ 
                    width: '100%', 
                    height: type === PostTypeConfig.Challenges ? { mobile: '100%', tablet: '768px' } : '100%', 
                    maxHeight: type === PostTypeConfig.Challenges ? { mobile: MAX_HEIGHT_IMG, tablet: '768px' } : { mobile: MAX_HEIGHT_IMG, tablet: '768px' } 
                }}>
                    <FallbackUI
                        description="표시할 미디어가 없습니다."
                        width={'100%'}
                        maxWidth={'100%'}
                        height={type === PostTypeConfig.Challenges ? { mobile: '335px', tablet: '768px' } : '335px'}
                        marginRight="0"
                    />
                </Box>
            </Grid>
        );
    if (data?.[0]?.mediaType === 'url') {
        const thumbNailYoutube = getYouTubeThumbnail((data?.[0]?.mediaUrl as string) ?? '');
        const videoId = extractVideoId((data?.[0]?.mediaUrl as string) ?? '');
        // Only allow play in detail view (when isBlockModalViewMedia is false)
        const canPlayVideo = !isBlockModalViewMedia;

        return (
            <CenteredBox
                sx={{
                    py: 0,
                    position: 'relative',
                    minHeight: '210px',
                    flexDirection: 'column',
                    height: '100%',
                    minWidth: MIN_WIDTH_IMG,
                    width: '100%',
                    background: theme.palette.background.default,
                }}
            >
                {!isYoutubePlaying || !canPlayVideo ? (
                    <CenteredBox
                        sx={{
                            py: 0,
                            position: 'relative',
                            minHeight: '210px',
                            flexDirection: 'column',
                            height: '100%',
                            minWidth: MIN_WIDTH_IMG,
                            width: '100%',
                            background: theme.palette.background.default,
                        }}
                    >
                        <Grid
                            item
                            className="thumbTiles"
                            style={{ backgroundColor: theme.palette.grayscale.gray02, display: 'flex', justifyContent: 'center', width: '100%' }}
                        >
                            <Box
                                position="relative"
                                sx={{ 
                                    width: '100%', 
                                    height: type === PostTypeConfig.Challenges ? { mobile: '100%', tablet: '768px' } : '100%', 
                                    maxHeight: type === PostTypeConfig.Challenges ? { mobile: MAX_HEIGHT_IMG, tablet: '768px' } : { mobile: MAX_HEIGHT_IMG, tablet: '768px' }, 
                                    cursor: canPlayVideo ? 'pointer' : 'default' 
                                }}
                                onClick={() => canPlayVideo && setIsYoutubePlaying(true)}
                            >
                                <BaseCardMedia
                                    src={thumbNailYoutube ?? ''}
                                    activeIndex={activeIndex}
                                    dataLength={data?.length}
                                    alt="Media Feed"
                                    mediaType={'image'}
                                    shouldShowNumbersImg={shouldShowNumbersImg}
                                    priority={priority}
                                    style={{ 
                                        minHeight: MIN_HEIGHT_IMG, 
                                        maxHeight: type === PostTypeConfig.Challenges && isTablet ? '540px' : (isTablet ? '768px' : MAX_HEIGHT_IMG),
                                        width: '100%', 
                                        objectFit: 'cover' 
                                    }}
                                    onError={() => null}
                                />
                                {/* Custom Play Button - only show in detail view */}
                                {canPlayVideo && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            zIndex: 10,
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <PlayButtonYoutube width="36" height="36" />
                                    </Box>
                                )}
                            </Box>
                        </Grid>
                    </CenteredBox>
                ) : (
                    <Box
                        sx={{
                            width: '100%',
                            height: '0',
                            paddingBottom: '56.25%', // 16:9 aspect ratio
                            position: 'relative',
                            backgroundColor: theme.palette.grayscale.gray02,
                        }}
                    >
                        <iframe
                            src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                border: 'none',
                            }}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    </Box>
                )}
            </CenteredBox>
        );
    }

    const showProductStatusOverlay = type === PostTypeConfig.Products && productStatus && (productStatus === 'reserved' || productStatus === 'sold');
    
    return (
        <Grid
            item
            className="thumbTiles"
            style={{ backgroundColor: theme.palette.grayscale.gray02, display: 'flex', justifyContent: 'center', width: '100%' }}
        >
            <Box
                position="relative"
                sx={{
                    width: '100%',
                    height: type === PostTypeConfig.Challenges ? { mobile: '100%', tablet: '768px' } : '100%',
                    maxHeight: type === PostTypeConfig.Challenges ? { mobile: MAX_HEIGHT_IMG, tablet: '768px' } : { mobile: MAX_HEIGHT_IMG, tablet: '768px' },
                    // Làm mờ hình khi reserved/sold
                    ...(showProductStatusOverlay && {
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.2)',
                            zIndex: 10,
                            pointerEvents: 'none',
                        }
                    })
                }}
            >
                <SliderMedia
                    ref={swiperRef}
                    data={data}
                    pagination={showBullet}
                    showNumbersImg={shouldShowNumbersImg}
                    showButtonNextPrev={false}
                    spaceBetween={0}
                    onTouchMoveStart={moveStarted}
                    slidesPerView={'auto'}
                    autoHeight={type === PostTypeConfig.Challenges ? !isTablet : true}
                    onActiveIndexChange={(index: number) => setActiveIndex(index)}
                    effect="slide"
                    allowTouchMove={true}
                    classForSlideItem={true}
                    onSlideChange={(index) => {}}
                    style={{ height: type === PostTypeConfig.Challenges && isTablet ? '768px' : 'auto' }}
                >
                    {(image, index, hasError, handleError, onReady) =>
                        hasError ? (
                            <FallbackUI
                                width={'100%'}
                                maxWidth={'100%'}
                                minHeight={type === PostTypeConfig.Challenges ? { mobile: '255px', tablet: '768px' } : '255px'}
                                height={type === PostTypeConfig.Challenges ? { mobile: MIN_HEIGHT_IMG, tablet: '768px' } : MIN_HEIGHT_IMG}
                                maxHeight="100%"
                                marginRight="0"
                                description="The url or image is invalid"
                            />
                        ) : (
                            <ModalViewsMedia
                                isBlockModalViewMedia={isBlockModalViewMedia}
                                mediaUrl={image.mediaUrl as string}
                                listMedias={data}
                                initialIndex={index}
                                key={image.mediaUrl}
                            >
                                <BaseCardMedia
                                    src={image.mediaMdUrl ?? image.mediaUrl}
                                    activeIndex={activeIndex}
                                    dataLength={data.length}
                                    handleVideoReady={onReady}
                                    onHandleMediaVideoProcess={onHandleMediaVideoProcess}
                                    mediaType={image.mediaType}
                                    shouldShowNumbersImg={shouldShowNumbersImg}
                                    alt="Media Feed"
                                    priority={priority && index === 0}
                                    style={{ 
                                        minHeight: MIN_HEIGHT_IMG, 
                                        height: type === PostTypeConfig.Challenges && isTablet ? '768px' : 'auto',
                                        maxHeight: type === PostTypeConfig.Challenges && isTablet ? '768px' : (isTablet ? '768px' : MAX_HEIGHT_IMG), 
                                        width: '100%', 
                                        objectFit: 'cover' 
                                    }}
                                    onError={handleError}
                                />
                            </ModalViewsMedia>
                        )
                    }
                </SliderMedia>
                {type === PostTypeConfig.Challenges && data.length > AbsoluteValue.Number && mode !== ChallengeMode.CONTINUOUS && endAt ? (
                    <Box
                        sx={{
                            width: 'fit-content',
                            height: '24px',
                            bottom: 20,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            borderRadius: '30px',
                            right: 20,
                            lineHeight: 0,
                            p: '7px 8px',
                            gap: '10px',
                            zIndex: 99,
                            position: 'absolute',
                            color: 'white',
                        }}
                    >
                        <Typography
                            variant="h2_10Regular"
                            // color="primary.contrastText"
                        >
                            {timeLeftRef.current.hours === 0 && timeLeftRef.current.minutes === 0 && timeLeftRef.current.seconds === 0
                                ? t('challenge_ended') || '챌린지 종료'
                                : `${t('time_remaining') || '남은 시간'} ${formatTime(timeLeftRef.current.hours)}:${formatTime(timeLeftRef.current.minutes)}:${formatTime(timeLeftRef.current.seconds)}`}
                        </Typography>
                    </Box>
                ) : null}

                {/* Product Status Overlay - Reserved/Sold */}
                {showProductStatusOverlay && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            backgroundColor: 'rgba(0, 0, 0, 0.3)',
                            borderRadius: '20px',
                            padding: '5px 24px',
                            zIndex: 20,
                            pointerEvents: 'none',
                            border: '1px solid #fff'
                        }}
                    >
                        <Typography
                            sx={{
                                color: '#fff',
                                fontSize: '14px',
                                fontWeight: 700,
                                letterSpacing: '-0.5px',
                            }}
                        >
                            {productStatus === 'reserved' ? t('product_reserved', '예약 중') : t('product_sold', '판매 완료')}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Grid>
    );
};

function areEqual(p: ImageGridAction, n: ImageGridAction) {
    const dataEqual = JSON.stringify(p.data) === JSON.stringify(n.data);
    const statusEqual = p.productStatus === n.productStatus;
    const typeEqual = p.type === n.type;
    const result = dataEqual && statusEqual && typeEqual;
    return result;
}

export default React.memo(ImageGridAction, areEqual);
